import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing, typography } = themeVariables;

/* Imported rather than restated, so the two cards cannot drift into reading as
   different objects: the preview is the same card, showing less of the review. The
   same reason `ProductCardSkeleton` imports its measurements from `ProductCard`. */
import { card as reviewCard } from '../ReviewCard/ReviewCard.styles';

export { date, ratingRow } from '../ReviewCard/ReviewCard.styles';

/**
 * The card fills the height it is given, the same way `ProductCard` does, so how tall it
 * is belongs to the consumer: a row that stretches its items gets cards with one bottom
 * edge, whatever mix of shapes it holds, and a column gets each card at its own height,
 * since against a parent with no height of its own `100%` resolves to `auto`. The card
 * reserves nothing itself, so a row that wants a floor sets one.
 *
 * Positioned so the whole-card link below can stretch over it.
 */
export const card = css`
  ${reviewCard}
  position: relative;
  height: 100%;
`;

/**
 * A "stretched link": one anchor whose pseudo-element covers the card, so a click anywhere
 * on a card with no control of its own opens the review, while keyboard and screen reader
 * users get one ordinary link. Rendered only when there is no other control, so a card
 * never carries two links to the same place.
 */
export const cardLink = css`
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::after {
    outline: var(${themeVariables.strokeWidth}) solid var(${themeVariables.colors.stroke.focus});
    outline-offset: 2px;
  }
`;

/**
 * Lines the review text clamps to, set per card from `getReviewLayout`: the shape's lines,
 * or one fewer when "Read more" is showing.
 *
 * A custom property rather than an interpolated `css` block, so four line counts do not
 * become four generated classes.
 */
export const TEXT_CLAMP_VAR = '--review-preview-card-text-clamp';

/* Pushes the footer to the bottom, so the card fills whatever height it is given and a
   row of cards lines its footers up. */
export const content = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: var(${spacing.sm});
  min-height: 0;
`;

export const header = css`
  display: flex;
  flex-direction: column;
  gap: var(${spacing.sm});
`;

/* Row rather than column, so a review with one photo alongside it puts the photo to the
   right of the text, as the reply variants do. With no photo the column simply takes the
   full width. */
export const textRow = css`
  display: flex;
  align-items: center;
  gap: var(${spacing.md});
`;

/**
 * The text and, when there is more to read, the link under it.
 *
 * No height of its own: the text clamps to the shape's lines and the column is as tall as
 * that. Any height the card is given beyond its content is absorbed by `content` between
 * the header and the footer, not here, so the text stays where it is when the card is
 * stretched.
 *
 * A card with a shown link is one `gap` taller than the same card with a hidden one, since
 * the column lays out the text and the link with this `gap` between them. That is the
 * only movement "Read more" causes, and a row with a floor of its own never shows it.
 *
 * `justify-content` keeps the text top-aligned in every shape: `textRow`'s own
 * `align-items: center` only keeps this column and the photo beside it on the same
 * center line, it does not center the text itself.
 */
export const textColumn = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(${spacing.xxs});
  justify-content: flex-start;
  min-width: 0;
`;

/**
 * The clamp is one line short of the shape's lines while "Read more" is showing, so the
 * link takes the line the text gave up: the text region has the same number of lines
 * whether or not the link is there.
 *
 * The clamp never lifts: "Read more" opens the review's own drawer rather than growing
 * the card, so a card's height never changes under the customer.
 */
export const text = css`
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  /* The same rule ReviewCard has, for the same reason: a review can be a single unbroken
     token, a pasted URL for instance, which would otherwise clip mid-token on the first
     line instead of wrapping across the lines the shape allows. */
  overflow-wrap: anywhere;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.md.fontSize});
  line-height: var(${typography.body.sizes.md.lineHeight});
  letter-spacing: var(${typography.body.letterSpacing});
  color: var(${colors.content.default});
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(${TEXT_CLAMP_VAR});
`;

/**
 * Body text that leads somewhere: the shared half of "Read more" and "View all photos".
 *
 * Exported for `ReviewPreviewCardPhotos`, which draws the other one. Each owns its own
 * `text-decoration` and its own place in the layout, which is all that differs between
 * them. `700` is a literal because the theme has one body weight and no bold step.
 */
export const bodyLink = css`
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.md.fontSize});
  font-weight: 700;
  line-height: var(${typography.body.sizes.md.lineHeight});
  letter-spacing: var(${typography.body.letterSpacing});
  color: var(${colors.content.link});
`;

export const readMore = css`
  ${bodyLink}
  align-self: flex-start;
  text-decoration: none;
`;
